import {NkCarousel} from '#build/components';

const globals = {
	floats: {
		left: 'float-left',
		right: 'float-right',
	},
	positions: {
		left: 'left-0',
		right: 'right-0',
		top: 'top-0',
		bottom: 'bottom-0',
	},
	aspectRatios: {
		auto: 'aspect-auto',
		'1/1': 'aspect-[1/1]',
		'16/9': 'aspect-[16/9]',
		'21/9': 'aspect-[21/9]',
		'4/3': 'aspect-[4/3]',
	},
	fontSizes: {
		xs: 'text-xs',
		sm: 'text-sm',
		base: 'text-base',
		md: 'text-md',
		lg: 'text-lg',
		xl: 'text-xl',
		'2xl': 'text-2xl',
		'3xl': 'text-3xl',
		'4xl': 'text-4xl',
		'5xl': 'text-5xl',
	},
	justifyContent: {
		normal: 'justify-normal',
		start: 'justify-start',
		end: 'justify-end',
		center: 'justify-center',
		between: 'justify-between',
		around: 'justify-around',
		evenly: 'justify-evenly',
		stretch: 'justify-stretch',
	},
};

const settings = {
	NkButton: {
		fixedClasses:
			'inline-flex items-center gap-2.5 transition duration-300 ease-out font-medium relative rounded-full no-underline',
		variants: {
			solid: 'nk-solid',
			outline: 'nk-outline border',
			link: 'nk-link flex justify-start items-center transition-all duration-150 relative left-0 hover:left-1',
			ghost: 'nk-ghost transition-all duration-150',
		},
		colors: {
			primary:
				'[&.nk-solid]:bg-secondary-500 [&.nk-solid]:hover:bg-secondary-400 [&.nk-solid]:focus:bg-secondary-400 [&.nk-solid]:text-neutral-900 [&.nk-link]:border-primary-element [&.nk-link]:text-primary-text [&.nk-link]:hover:text-primary-text--hover [&.nk-link]:hover:border-primary-element--hover [&.nk-ghost]:text-primary-text [&.nk-ghost]:hover:text-primary-text--hover [&.nk-ghost]:hover:border-primary-element--hover [&.nk-outline]:border-primary-element [&.nk-outline]:text-primary-text [&.nk-outline]:hover:text-primary-text--hover [&.nk-outline]:hover:border-primary-element--hover',
			secondary:
				'[&.nk-solid]:bg-secondary-element [&.nk-solid]:hover:bg-secondary-element--hover [&.nk-solid]:focus:bg-secondary-element--active [&.nk-solid]:text-secondary-on-element [&.nk-link]:border-secondary-element [&.nk-link]:text-secondary-500 [&.nk-link]:hover:text-secondary-500 [&.nk-link]:hover:border-secondary-element--hover [&.nk-ghost]:text-secondary-500 [&.nk-ghost]:hover:text-secondary-300 [&.nk-ghost]:hover:border-secondary-element--hover [&.nk-outline]:border-secondary-element [&.nk-outline]:text-secondary-text [&.nk-outline]:hover:text-secondary-text--hover [&.nk-outline]:hover:border-secondary-element--hover',
		},
		sizes: {
			default: 'px-5 py-3 text-base',
			sm: 'p-2.5',
			collapse: 'p-0',
		},
	},
	NkBreadcrumb: {
		classes: {
			wrapper: 'container -mx-1 hidden lg:block',
			container: '',
			list: 'flex justify-start items-center list-none truncate',
			item: 'font-normal text-white/80 py-1 px-1 inline items-center',
			link: 'nav-link flex justify-start items-center text-white/80 inline items-center [a&]:text-white [a&]:hover:underline [a&]:underline-offset-4 link-active:text-white/80 link-active:pointer-events-none',
			currentPage: 'flex justify-start items-center gap-2',
			homeTitle: 'hidden sm:flex justify-start items-center gap-2 -mr-1',
		},
		homeIcon: {
			icon: 'home',
			variant: 'boxed',
			backgroundColor: true,
			color: 'primary',
			class: 'mr-1',
		},
	},
	NkContainer: {
		variants: {
			default: 'container py-7 px-5',
			fluid: 'max-w-full',
		},
	},
	NkImage: {
		classes: {
			wrapper: 'relative h-full',
			copyright:
				'inline-block absolute z-30 opacity-75 leading-none rounded-full p-1 text-white text-sm',
			container: 'block relative w-full h-full',
			image:
				'max-w-full w-full lazyload transition-opacity duration-500 ease-out delay-100',
			noteLabel:
				'absolute bottom-0 right-0 inline-flex items-center bg-black bg-opacity-75 m-2 px-2 py-1 text-white text-sm rounded whitespace-no-wrap',
			noteLabelIcon: 'mr-1 text-base',
			figcaption: 'text-sm text-neutral-800 pt-2.5',
		},
		aspectRatios: globals.aspectRatios,
		positions: globals.positions,
		floats: globals.floats,
		imageSrcset: [480, 640, 768, 1024, 1280, 1920],
	},
	NkCarousel: {
		variants: {
			ghost: {
				sliderOptions: {
					spaceBetween: 0,
					slidesPerView: 1,
					centeredSlides: true,
					autoplay: {
						delay: 5000,
					},
				},
				wrapper: 'relative',
				slider: 'sm:overflow-hidden',
				sliderItem: 'flex items-center justify-center',
				slideContent: 'h-full w-full pb-20',
				slideBackgroundImage: '',
				controls:
					'absolute bottom-5 w-full z-10 flex justify-center items-center gap-7 pl-1/2 lg:pl-[calc(100%*2/7)]',
				buttonWrapper: '',
				button:
					'transition ease-out duration-300 flex justify-center items-center text-center text-neutral-900 hover:text-neutral-900/60 text-lg',
				buttonLeft: 'left-0 hidden',
				buttonLeftIcon: 'chevron-left',
				buttonRight: 'right-0 hidden',
				buttonRightIcon: 'chevron-right',
				buttonDisabled: 'opacity-50 cursor-not-allowed',
				paginationWrapper: 'flex justify-center items-center gap-2 z-20',
				pagination:
					'flex justify-center items-center gap-2.5 md:justify-start list-none',
				paginationButton:
					'block size-3.5 rounded-full bg-neutral-900/20 hover:bg-neutral-900/40 is-active:bg-neutral-900 is-active:hover:bg-neutral-900/60 transition',
				paginationButtonActive: 'is-active',
			},
			topThema: {
				sliderOptions: {
					spaceBetween: 0,
					slidesPerView: 1,
					centeredSlides: true,
					autoplay: {
						delay: 5000,
					},
				},
				wrapper: 'relative bg-secondary-500',
				slider: 'sm:overflow-hidden',
				sliderItem: 'flex items-center justify-center',
				slideContent: 'h-full w-full',
				slideBackgroundImage: '',
				controls:
					'absolute bottom-7 w-full z-10 flex justify-center items-center gap-7',
				buttonWrapper: '',
				button:
					'transition ease-out duration-300 flex justify-center items-center text-center text-neutral-900 hover:text-neutral-900/60 text-lg',
				buttonLeft: 'left-0 hidden',
				buttonLeftIcon: 'chevron-left',
				buttonRight: 'right-0 hidden',
				buttonRightIcon: 'chevron-right',
				buttonDisabled: 'opacity-50 cursor-not-allowed',
				paginationWrapper: 'flex justify-center items-center gap-2 z-20',
				pagination:
					'flex justify-center items-center gap-2.5 md:justify-start list-none',
				paginationButton:
					'block size-3.5 rounded-full bg-neutral-900/20 hover:bg-neutral-900/40 is-active:bg-neutral-900 is-active:hover:bg-neutral-900/60 transition',
				paginationButtonActive: 'is-active',
			},
			hero: {
				sliderOptions: {
					spaceBetween: 0,
					slidesPerView: 1,
					speed: 600,
					centeredSlides: true,
					navigation: false,
					effect: 'slide',
					autoplay: {
						delay: 6000,
					},
					fadeEffect: {
						crossFade: true,
					},
				},
				wrapper: 'relative w-full bg-secondary-surface overflow-hidden',
				slider: 'h-full min-h-full',
				sliderItem: 'flex items-center lg:aspect-[11/4]',
				slideContent: 'relative z-10 h-full',
				slideBackgroundImage: 'absolute top-0 left-0 h-full w-full',
				controls:
					'absolute flex z-10 w-full lg:w-1/3 top-auto lg:top-2/3 bottom-10 lg:bottom-auto -translate-y-1/2 left-0 px-3 justify-center items-center gap-4',
				buttonWrapper: 'flex',
				button:
					'transition ease-out duration-300 justify-center items-center text-center text-white/50 text-lg hover:text-white/75 focus:text-white inline-flex',
				buttonLeft: '',
				buttonLeftIcon: 'chevron-left',
				buttonRight: '',
				buttonRightIcon: 'chevron-right',
				buttonDisabled: 'opacity-50 cursor-not-allowed pointer-events-none',
				paginationWrapper: '',
				pagination:
					'flex flex-wrap items-center list-none justify-center gap-2.5',
				paginationButton:
					'block w-3.5 h-3.5 rounded-full bg-white/50 hover:bg-white/75 is-active:bg-white transition-all',
				paginationButtonActive: 'is-active',
			},
			'hero-slim': {
				sliderOptions: {
					spaceBetween: 0,
					slidesPerView: 1,
					speed: 600,
					centeredSlides: true,
					navigation: false,
					effect: 'fade',
					autoplay: {
						delay: 6000,
					},
					fadeEffect: {
						crossFade: true,
					},
				},
				wrapper:
					'relative w-full bg-secondary-surface overflow-hidden after:content-[""] after:absolute after:left-0 after:top-0 after:w-full after:h-full after:bg-primary-500 lg:after:bg-primary-500/90 after:z-10 before:absolute before:left-0 before:-bottom-0.5 before:w-[calc(100%+2px)] before:h-full before:lg:bg-[url("/public/images/layout/marxzell__deko-element-hero.svg")] before:bg-no-repeat before:bg-bottom before:bg-contain before:z-20',
				slider: 'h-full min-h-full',
				sliderItem: 'flex items-center h-20 lg:h-full lg:aspect-[4.5/1]',
				slideContent:
					'relative z-10 py-10 px-3 max-w-screen-sm mx-auto text-center text-shadow-sm flex h-full items-center justify-center',
				slideBackgroundImage:
					'absolute top-0 left-0 object-cover lazyload h-full w-full',
				controls: 'hidden',
				buttonWrapper:
					'absolute bottom-0 right-0 hidden my-6 px-3 translate-y-1/2 transform z-20',
				button:
					'transition ease-out duration-300 justify-center items-center text-center text-white/50 text-xl hover:text-white/75 focus:text-white hidden sm:inline-flex',
				buttonLeft: '',
				buttonLeftIcon: 'chevron-left',
				buttonRight: '',
				buttonRightIcon: 'chevron-right',
				buttonDisabled: 'opacity-50 cursor-not-allowed pointer-events-none',
				paginationWrapper:
					'absolute w-full bottom-0 block z-10 my-6 translate-y-1/2 transform',
				pagination:
					'flex flex-wrap items-center list-none -mx-1 justify-center',
				paginationButton:
					'block w-8 is-active:w-10 h-3 mx-1 rounded bg-white/50 hover:bg-white/75 is-active:bg-white transition-all',
				paginationButtonActive: 'is-active',
			},
		},
	},
	NkDate: {
		variants: {
			default: {
				wrapper:
					'inline-flex flex-col justify-center items-center bg-neutral-50 size-16 min-w-16 pl-0 pr-0 pt-0 pb-0 rounded-full',
				day: 'text-base font-bold',
				month: 'text-sm uppercase tracking-wide',
			},
		},
	},
	NkDrilldown: {
		fixedClasses: '',
		variants: {
			default: {
				menu: 'nk-drilldown bg-neutral-surface rounded overflow-hidden',
				menuInner: 'list-none',
				menuItem: 'border-b border-neutral-border last:border-0',
				menuItemButton:
					'relative flex w-full py-4 px-4 text-primary-text hover:bg-neutral-surface-hover',
				menuItemButtonActive:
					'border-b border-neutral-border text-primary-text',
				menuItemIcon: 'no-underline before:no-underline after',
				menuItemButtonIcon:
					'ml-auto no-underline absolute right-0 mr-3 transform transition-transform duration-300 ease-in-out ',
				menuItemButtonIconActive: 'rotate-180',
				menuItemLink:
					'block py-4 px-4 hover:bg-neutral-100 link-active:bg-primary-element link-active:text-neutral-50 link-active:hover:text-neutral-50 link-active:hover:bg-primary-400 link-active-exact:bg-primary-element link-active-exact:text-primary-on-element group-[.is-submenu]:pl-8',
				menuItemLinkIcon: 'ml-auto no-underline before:no-underline',
				submenu: 'bg-neutral-surface--active group is-submenu',
			},
		},
	},
	NkPagination: {
		classes: {
			wrapper: 'my-3',
			list: 'flex items-center list-none -mx-1',
			item: 'text-sm px-1',
			link: 'inline-flex justify-center items-center text-type text-center focus:text-neutral-900 hover:bg-neutral-100 size-8 rounded-full',
			linkActive:
				'inline-flex justify-center items-center text-center text-primary-500 hover:text-primary-500 hover:bg-neutral-100 size-8 rounded-full font-bold',
			button:
				'inline-flex p-3 items-center justify-center bg-secondary-500 text-neutral-900 hover:bg-secondary-400 rounded-full',
			buttonDisabled:
				'inline-flex bg-neutral-100 text-neutral-400 hover:text-neutral-400 p-3 items-center justify-center cursor-not-allowed rounded-full',
			currentItem: '',
		},
		justifyContent: globals.justifyContent,
		firstPageIcon: 'firstPage',
		previousPageIcon: 'previousPage',
		nextPageIcon: 'nextPage',
		lastPageIcon: 'lastPage',
	},
	NkList: {
		fixedClasses: {
			wrapper: 'list-outside [&.list-disc]:pl-4 [&.list-decimal]:pl-4',
		},
		variants: {
			unordered: {wrapper: 'list-disc'},
			ordered: {wrapper: 'list-decimal'},
			clean: {wrapper: 'list-none flex flex-col gap-0.5 py-2', item: 'py-1.5'},
			table: {
				wrapper: 'list-none pl-3',
				item: 'border-b border-neutral-border py-2',
			},
			horizontal: {
				wrapper: 'md:flex flex-wrap list-none -mx-2 -mt-1 mb-1',
				item: 'inline-flex items-center px-2 py-1',
			},
			overview: {
				wrapper: 'list-none block -my-4',
				item: 'py-8 border-b border-neutral-border last:border-b-0',
			},
		},
	},
	NkLink: {
		fixedClasses: '',
		classes: 'underline text-primary-element hover:text-primary-element--hover',
	},
	NkFilterbar: {
		variants: {
			default: {
				wrapper: 'py-3',
			},
		},
	},
	NkForm: {
		fixedClasses: {
			wrapper: 'w-full max-w-[940px]',
			form: '',
		},
		variants: {
			default: {
				wrapper: '',
				form: '',
			},
			boxed: {
				wrapper: '',
				form: 'block bg-neutral-surface p-6 rounded',
			},
		},
	},
	NkWrapper: {
		spacings: {
			margin: {
				xs: 'my-1',
				sm: 'my-2',
				default: 'my-4',
				md: 'my-6',
				lg: 'my-10',
				xl: 'my-12',
				'2xl': 'my-16',
			},
			padding: {
				xs: 'py-1',
				sm: 'py-2',
				default: 'py-3',
				md: 'py-4',
				lg: 'py-6',
				xl: 'py-8',
				'2xl': 'py-10',
			},
		},
	},
	NkNav: {
		fixedClasses: '',
		variants: {
			default: {
				wrapper: '',
				item: 'border-b border-neutral-border text-md',
				title: 'font-bold',
				text: '',
				content: 'inline-block text-base underline',
				link: 'block py-3 px-2 transition duration-300 group text-primary-text hover:text-primary-text--hover link-active:bg-primary-surface link-active:text-primary-on-surface',
				prependIcon: {
					icon: 'chevron-right',
					class: 'mr-1',
				},
			},
			footprint: {
				wrapper: 'flex flex-wrap divide-x divide-neutral-border -mx-2',
				item: '',
				title: '',
				text: '',
				link: 'flex items-center py-1 px-2 text-primary-text hover:text-primary-text--hover transition duration-300 group',
				prependIcon: false,
			},
			resources: {
				wrapper: 'border border-neutral-border rounded-sm',
				item: 'border-b border-neutral-border',
				link: 'flex items-center flex-no-wrap p-3 text-primary-text hover:text-primary-text--hover  transition duration-300 group no-underline',
				content: 'px-3 flex-1 overflow-hidden underline',
				longtext: 'whitespace-normal',
				prependIcon: {
					icon: 'file',
					color: 'primary-500',
					size: 'xl',
					inline: false,
				},
				appendIcon: {
					icon: 'download',
					color: 'type',
					size: 'xl',
					inline: false,
				},
			},
			links: {
				wrapper: 'border border-neutral-border rounded-sm',
				item: 'border-b border-neutral-border',
				link: 'flex items-center flex-no-wrap py-3 px-2 text-primary-text hover:text-primary-text--hover transition duration-300 group no-underline',
				content: 'truncate px-3 underline',
				text: 'italic truncate',
				appendIcon: {
					icon: 'chevron-right',
					color: 'primary-500',
					size: 'xl',
					inline: false,
				},
			},
			abc: {
				wrapper: 'flex flex-wrap justify-start',
				item: '',
				title: '',
				text: '',
				link: 'flex justify-center items-center size-10 rounded-full font-medium text-primary-500 no-underline hover:bg-neutral-100 link-active:bg-secondary-500'
			},
		},
	},
	NkTemplateAbfalltermine: {
		fixedClasses: '',
		variants: {
			default: {
				wrapper: '',
				section: '',
				headline1: '',
				headline2: '',
				terminContainer: '',
				terminRow: 'grid grid-cols-12 gap-5',
				termin: 'nk-abfalltermin col-span-12 lg:col-span-6 p-5 bg-white',
			},
		},
	},
	NkTemplateAccessibility: {
		classes: {
			accessibilityLink:
				'text-secondary-500 focus:text-secondary-400 focus:outline-secondary-500 hover:text-secondary-400',
			container:
				'container bg-neutral-900 text-neutral-50 [&_a]:text-secondary-500 [&_a:hover]:text-secondary-400 [&_a:focus]:text-secondary-400 [&_a:focus]:outline-secondary-500 [&_p]:text-neutral-50',
		},
	},
};

export {settings, globals};
